<template>
  <div class="container page-py mobile-container">
    <h2>
      {{ $t('creatingLayout') }}
    </h2>
    <div style="scroll-behavior: smooth">
      <div class="main-info">
        <div
          id="room-layout-mode"
          ref="room-layout-mode"
          class="mb-10"
        >
          <h3 id="howToSwitch">
            {{ $t('howToSwitchRoomLayout') }}
          </h3>
          <p class="mb-5">
            {{ $t('firstCreateRoomLayout') }}<br />
            {{ $t('switchDesignModeInstr') }}<br />
            {{ $t('editLayoutStep') }}
          </p>
          <img
            v-if="locale === 'ru'"
            :src="`${staticUrl}/images/sc-plan-1.webp`"
            alt=""
            class="article-image mt-5"
          />
          <img
            v-else
            :src="`${staticUrl}/images/sc-plan-en-1.webp`"
            alt=""
            class="article-image"
          />
        </div>
        <div ref="menu" class="mb-10">
          <h3 id="menu">
            {{ $t('menu') }}
          </h3>
          <img
            v-if="locale === 'ru'"
            :src="`${staticUrl}/images/sc-plan-2.webp`"
            alt=""
            class="article-image"
          />
          <img
            v-else
            :src="`${staticUrl}/images/sc-plan-en-2.webp`"
            alt=""
            class="article-image"
          />
        </div>
        <div
          id="building-a-room-plan"
          ref="building-a-room-plan"
          class="paragraph-block"
        >
          <h3 id="creatingRoomLayout">
            {{ $t('creatingRoomLayout') }}
          </h3>
          <p>
            {{ $t('startWorking') }}
          </p>
          <div
            id="loading-the-substrate"
            ref="loading-the-substrate"
            class="sub-paragraph-block"
          >
            <h4>
              {{ $t('upldBackRef') }}
            </h4>
            <div class="paragraph-steps">
              <p>
                {{ $t('upldJpgPng') }}
              </p>
            </div>
          </div>
          <div
            id="scaling-the-substrate"
            ref="scaling-the-substrate"
            class="sub-paragraph-block"
          >
            <h4>
              {{ $t('scalBackRef') }}
            </h4>
            <div class="paragraph-steps">
              <p>
                {{ $t('scaleLayoutBackground') }}
              </p>
            </div>
            <video
              v-if="locale === 'ru'"
              class="video-loop"
              no-controls
              autoplay
              loop
              playsinline
              muted
            >
              <source
                :src="`${staticUrl}/video/SC_3.mp4`"
                type="video/mp4"
              />
            </video>
            <div v-else class="image-carousel">
              <ImagesCustomSlider
                :height="null"
                width="100%"
                :images="[
                  `${staticUrl}/images/sc-plan-en-3-1.webp`,
                  `${staticUrl}/images/sc-plan-en-3-2.webp`,
                ]"
              />
            </div>
          </div>
          <div
            id="creating-a-room-plan"
            ref="creating-a-room-plan"
            class="sub-paragraph-block"
          >
            <h4>
              {{ $t('creatLayPrecDraw') }}
            </h4>
            <div class="paragraph-steps">
              <p>
                {{ $t('createRoom') }}
              </p>
              <p>
                {{ $t('useRulerGuidesEtc') }}
              </p>
              <p>
                {{ $t('stepByStep') }}
              </p>
              <div class="step-block">
                <p class="step-element">
                  {{ $t('innerOutline') }}
                </p>
                <p class="step-element">
                  {{ $t('drawGeneralOuterOutlinesRoom') }}
                </p>
                <p class="step-element">
                  {{ $t('deleteCreatedOutlines') }}
                </p>
                <p class="step-element">
                  {{ $t('setTheCeilingHeight') }}
                </p>
              </div>
            </div>
            <div v-if="locale === 'ru'" class="image-carousel">
              <ImagesCustomSlider
                :height="null"
                :width="'100%'"
                :images="[
                  `${staticUrl}/images/sc-plan-4-1.webp`,
                  `${staticUrl}/images/sc-plan-4-2.webp`,
                ]"
              />
            </div>
            <div v-else class="image-carousel">
              <ImagesCustomSlider
                :height="null"
                :width="'100%'"
                :images="[
                  `${staticUrl}/images/sc-plan-en-4-1.webp`,
                  `${staticUrl}/images/sc-plan-en-4-2.webp`,
                ]"
              />
            </div>
          </div>
          <div
            id="viewing-and-navigation"
            ref="viewing-and-navigation"
            class="sub-paragraph-block"
          >
            <h4>
              {{ $t('viewNavigate') }}
            </h4>
            <div class="paragraph-steps">
              <div class="step-block">
                <p>
                  {{ $t('repositionLayout') }}
                </p>
                <p>
                  {{ $t('rotateMouseZoom') }}
                </p>
                <p>
                  {{ $t('holdDownMouseMove') }}
                </p>
              </div>
            </div>
            <video
              v-if="locale === 'ru'"
              class="video-loop"
              no-controls
              autoplay
              loop
              playsinline
              muted
            >
              <source
                :src="`${staticUrl}/video/SC_plan_01.mp4`"
                type="video/mp4"
              />
            </video>
            <video
              v-else
              class="video-loop"
              no-controls
              autoplay
              loop
              playsinline
              muted
            >
              <source
                :src="`${staticUrl}/video/SC_plan_01_en.mp4`"
                type="video/mp4"
              />
            </video>
          </div>
          <div
            id="doorway-creation-mode"
            ref="doorway-creation-mode"
            class="sub-paragraph-block"
          >
            <h4>
              {{ $t('createDoorwaysMode') }}
            </h4>
            <div class="paragraph-steps">
              <p>
                {{ $t('areaWantDoor') }}
              </p>
              <p>
                {{ $t('doorCreatedAutomatically') }}
              </p>
              <p>
                {{ $t('doorwayOptions') }}
              </p>
              <p>
                {{ $t('deleteCreatedDoorsInstruction') }}
              </p>
            </div>
            <video
              v-if="locale === 'ru'"
              class="video-loop"
              no-controls
              autoplay
              loop
              playsinline
              muted
            >
              <source
                :src="`${staticUrl}/video/SC_plan_02.mp4`"
                type="video/mp4"
              />
            </video>
            <video
              v-else
              class="video-loop"
              no-controls
              autoplay
              loop
              playsinline
              muted
            >
              <source
                :src="`${staticUrl}/video/SC_plan_02_en.mp4`"
                type="video/mp4"
              />
            </video>
          </div>
          <div
            id="window-openings-creation-mode"
            ref="window-openings-creation-mode"
            class="sub-paragraph-block"
          >
            <h4>
              {{ $t('windowOpeningsMode') }}
            </h4>
            <div class="paragraph-steps">
              <p>
                {{ $t('selectCreateWindOpenMode') }}
              </p>
              <p>
                {{ $t('windowCreateAutomatic') }}
              </p>
              <p>
                {{ $t('widthHeightOpening') }}
              </p>
              <p>
                {{ $t('deleteCreatedWindows') }}
              </p>
            </div>
            <video
              v-if="locale === 'ru'"
              class="video-loop"
              no-controls
              autoplay
              loop
              playsinline
              muted
            >
              <source
                :src="`${staticUrl}/video/SC_plan_03.mp4`"
                type="video/mp4"
              />
            </video>
            <video
              v-else
              class="video-loop"
              no-controls
              autoplay
              loop
              playsinline
              muted
            >
              <source
                :src="`${staticUrl}/video/SC_plan_03_en.mp4`"
                type="video/mp4"
              />
            </video>
          </div>
          <div
            id="3d-preview-mode"
            ref="3d-preview-mode"
            class="sub-paragraph-block"
          >
            <h4>
              {{ $t('Preview3DMode') }}
            </h4>
            <div class="paragraph-steps">
              <p>
                {{ $t('beforeYouStartWorkingInterior') }}
              </p>
              <p>
                {{ $t('return2DIcon') }}
              </p>
            </div>
            <img
              v-if="locale === 'ru'"
              :src="`${staticUrl}/images/sc-plan-5.webp`"
              alt=""
              class="article-image"
            />
            <img
              v-else
              :src="`${staticUrl}/images/sc-plan-en-5.webp`"
              alt=""
              class="article-image"
            />
          </div>
        </div>

        <div id="save-plan" ref="save-plan" class="paragraph-block">
          <h3 id="saveRoomLayoutWithoutNumber">
            {{ $t('saveRoomLayoutWithoutNumber') }}
          </h3>
          <div class="sub-paragraph-block">
            <div class="paragraph-steps">
              <p>
                {{ $t('saveRoomLayoutWithoutNumberT1') }}
              </p>
              <p>
                {{ $t('myProjects') }}
              </p>
            </div>
            <img
              v-if="locale === 'ru'"
              :src="`${staticUrl}/images/sc-plan-6.webp`"
              alt=""
              class="article-image"
            />
            <img
              v-else
              :src="`${staticUrl}/images/sc-plan-en-6.webp`"
              alt=""
              class="article-image"
            />
            <div class="i-block">
              <div class="i-block-info">
                <div class="i-block-info-icon">
                  <div class="info-icon-background">
                    <div class="info-icon-text">
                      {{ $t('important') }}
                    </div>
                  </div>
                </div>
                <div class="i-block-info-text">
                  {{ $t('twoSavingOptions') }}
                </div>
                <div class="i-block-info-text">
                  {{ $t('saveProjectModal') }}
                </div>
                <div class="i-block-info-text">
                  {{ $t('newProjectSaveProjectModal') }}
                </div>
              </div>
            </div>
            <img
              v-if="locale === 'ru'"
              :src="`${staticUrl}/images/sc-plan-7.webp`"
              alt=""
              class="article-image"
            />
            <img
              v-else
              :src="`${staticUrl}/images/sc-plan-en-7.webp`"
              alt=""
              class="article-image"
            />
          </div>

          <div class="sub-paragraph-block">
            <h3 id="savingEditingExistingLayout">
              {{ $t('editingExistingLayoutWithoutNumber') }}
            </h3>
            <div class="paragraph-steps">
              <p>
                {{ $t('myProjectsand') }}
              </p>
              <p>{{ $t('saveRoomLayoutWithoutNumberT1') }}</p>
            </div>
            <img
              v-if="locale === 'ru'"
              :src="`${staticUrl}/images/sc-plan-8.webp`"
              alt=""
              class="article-image"
            />
            <img
              v-else
              :src="`${staticUrl}/images/sc-plan-en-8.webp`"
              alt=""
              class="article-image"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import {onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useHead, useRuntimeConfig} from '#imports';

export default {
  setup() {
    const {t} = useI18n();
    const isShowFeedBackModal = ref(false);
    const runtimeConfig = useRuntimeConfig();
    const staticUrl = runtimeConfig.public.vueAppStaticUrl;

    useHead({
      title: `${t('DreamFlat')} - ${t('creatingLayout')}`,
      meta: [
        {
          name: 'description',
          content: `${t('creatingLayoutMetaDesc')}`,
        },
      ],
    });
    useSeoMeta({
      ogTitle: `${t('DreamFlat')} - ${t('creatingLayout')}`,
      description: `${t('creatingLayoutMetaDesc')}`,
      ogDescription: `${t('creatingLayoutMetaDesc')}`,
      ogImage: 'https://dreamflat.design/prev.webp',
    });

    const {locale} = useI18n();

    return {
      slide: ref(1),
      locale,
      mobileNavMenu: ref(false),
      isShowFeedBackModal,
      staticUrl,
    };
  },
};
</script>
<style lang="scss" scoped>
@import 'css/help-center/help-center-general-ctyle';
@media (max-width: $lg) {
  .main-info {
    width: 100%;
  }
  .table-contents {
    width: 100%;
  }
  .mobile-container {
    padding: 0 16px;
    margin-top: 32px;
  }
}
</style>
